import "zod/compile";
import { z } from "zod";

/**
 * Short Codes and Code Schemes. Terms follow docs/short-codes/CONTEXT.md.
 */

/** Every entity type that can carry Short Codes. The backend registry is keyed by it. */
export const SHORT_CODE_ENTITY_TYPES = ["warehouse", "division"] as const;
export const ShortCodeEntityTypeSchema = z.enum(SHORT_CODE_ENTITY_TYPES);
export type ShortCodeEntityType = z.infer<typeof ShortCodeEntityTypeSchema>;

/** Relation name to target entity type, per entity type. */
export type ShortCodeRelations = Readonly<
  Record<ShortCodeEntityType, Readonly<Record<string, ShortCodeEntityType>>>
>;

/**
 * The relations a Reference segment may follow. The backend registry names the
 * foreign key behind each one, so adding a relation here fails the backend
 * build until it does.
 */
export const SHORT_CODE_RELATIONS = {
  warehouse: { division: "division" },
  division: {},
} as const satisfies ShortCodeRelations;

export type ShortCodeRelationName<T extends ShortCodeEntityType> = Extract<
  keyof (typeof SHORT_CODE_RELATIONS)[T],
  string
>;

export const CodeSchemeIdSchema = z.uuid().brand<"CodeSchemeId">();
export type CodeSchemeId = z.infer<typeof CodeSchemeIdSchema>;

export const ShortCodeIdSchema = z.uuid().brand<"ShortCodeId">();
export type ShortCodeId = z.infer<typeof ShortCodeIdSchema>;

/**
 * An organization's handle for a scheme, unique per entity type. Workflow
 * config names schemes by key, so published workflow versions hold no ids.
 */
export const CodeSchemeKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{1,62}$/)
  .brand<"CodeSchemeKey">();
export type CodeSchemeKey = z.infer<typeof CodeSchemeKeySchema>;

const SegmentNameSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,31}$/);

/**
 * The longest Short Code value. Codes are short, and the cap bounds the work
 * reading one can take.
 */
export const MAX_SHORT_CODE_LENGTH = 64;

/** Widest counter whose values stay exact as a JavaScript number. */
export const MAX_COUNTER_WIDTH = 15;

export const LiteralSegmentSchema = z.object({
  kind: z.literal("literal"),
  text: z.string().min(1).max(32),
});

/**
 * A number exactly `width` digits wide, so width 4 counts to 9999. It counts
 * up within its `scope`: the segments whose values start it again at 1, as a
 * `["year"]` scope numbers each year from 1.
 */
export const CounterSegmentSchema = z.object({
  kind: z.literal("counter"),
  width: z.int().min(1).max(MAX_COUNTER_WIDTH),
  scope: z.array(SegmentNameSchema).max(12).optional(),
});

export const DATE_FORMATS = ["YYYY", "YY", "MM", "DD"] as const;

/** Part of the date a code is issued on, in the organization's time zone. */
export const DateSegmentSchema = z.object({
  kind: z.literal("date"),
  name: SegmentNameSchema,
  format: z.enum(DATE_FORMATS),
});

export const PartSegmentSchema = z.object({
  kind: z.literal("part"),
  name: SegmentNameSchema,
  /** A regex fragment, matched case-sensitively: write `[kK]` for either case. */
  pattern: z.string().min(1).max(128),
});

export const ReferenceSegmentSchema = z.object({
  kind: z.literal("reference"),
  name: SegmentNameSchema,
  /** A key of `SHORT_CODE_RELATIONS` for the scheme's entity type. */
  relation: z.string().min(1),
  /** Key of a scheme on the relation's target type; only its issued codes match. */
  scheme: CodeSchemeKeySchema,
});

export const SegmentSchema = z.discriminatedUnion("kind", [
  LiteralSegmentSchema,
  CounterSegmentSchema,
  DateSegmentSchema,
  PartSegmentSchema,
  ReferenceSegmentSchema,
]);
export type Segment = z.infer<typeof SegmentSchema>;
export type CounterSegment = z.infer<typeof CounterSegmentSchema>;
export type DateSegment = z.infer<typeof DateSegmentSchema>;
export type PartSegment = z.infer<typeof PartSegmentSchema>;
export type ReferenceSegment = z.infer<typeof ReferenceSegmentSchema>;

export const CodeSchemeDraftSchema = z.object({
  entityType: ShortCodeEntityTypeSchema,
  key: CodeSchemeKeySchema,
  name: z.string().trim().min(1).max(255),
  segments: z.array(SegmentSchema).min(1).max(12),
});
export type CodeSchemeDraft = z.infer<typeof CodeSchemeDraftSchema>;

/** A stored Code Scheme. `isDisplay` marks the one whose codes are Display Codes. */
export const StoredCodeSchemeSchema = CodeSchemeDraftSchema.extend({
  id: CodeSchemeIdSchema,
  isDisplay: z.boolean(),
});
export type StoredCodeScheme = z.infer<typeof StoredCodeSchemeSchema>;

export const SHORT_CODE_ORIGINS = ["imported", "generated"] as const;
export const ShortCodeOriginSchema = z.enum(SHORT_CODE_ORIGINS);
export type ShortCodeOrigin = z.infer<typeof ShortCodeOriginSchema>;

/** A Reference part: the referenced code as written and the entity it names. */
export const ReferencePartSchema = z.object({
  code: z.string(),
  entityId: z.uuid(),
});
export type ReferencePart = z.infer<typeof ReferencePartSchema>;

/** A parsed code's parts by segment name: a Part's or Date's text, or a Reference. */
export const ShortCodePartsSchema = z.record(
  z.string(),
  z.union([z.string(), ReferencePartSchema]),
);
export type ShortCodeParts = z.infer<typeof ShortCodePartsSchema>;

export const SchemeNodeSchema = z.object({
  entityType: ShortCodeEntityTypeSchema,
  key: CodeSchemeKeySchema,
});
export type SchemeNode = z.infer<typeof SchemeNodeSchema>;

/** Why a Code Scheme draft can't be saved, as `validateScheme` reports it. */
export const SchemeIssueSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("duplicate_segment_name"), name: z.string() }),
  z.object({ kind: z.literal("multiple_counters") }),
  /** A counter's scope names a segment the scheme has no Part, Date or Reference for. */
  z.object({ kind: z.literal("unknown_counter_scope"), name: z.string() }),
  /** The segments together could split a value too many ways to read it quickly. */
  z.object({ kind: z.literal("too_ambiguous") }),
  z.object({
    kind: z.literal("unsafe_pattern"),
    segment: z.string(),
    reason: z.string(),
  }),
  z.object({
    kind: z.literal("unknown_relation"),
    segment: z.string(),
    relation: z.string(),
  }),
  z.object({
    kind: z.literal("referenced_scheme_missing"),
    segment: z.string(),
    entityType: ShortCodeEntityTypeSchema,
    scheme: CodeSchemeKeySchema,
  }),
  z.object({
    kind: z.literal("reference_cycle"),
    cycle: z.array(SchemeNodeSchema),
  }),
]);
export type SchemeIssue = z.infer<typeof SchemeIssueSchema>;

/** Creating a scheme also fails when its key is already used for the entity type. */
export const CreateSchemeIssueSchema = z.discriminatedUnion("kind", [
  ...SchemeIssueSchema.options,
  z.object({ kind: z.literal("key_taken") }),
]);
export type CreateSchemeIssue = z.infer<typeof CreateSchemeIssueSchema>;
